import React from "react";
import { Phone, Star, Sparkles, MapPin } from "lucide-react";

export default function Hero() {
  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-20 md:pb-32 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/32644774/pexels-photo-32644774.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Premium black car detailed"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-zinc-950" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8 fade-up">
            {/* Rating badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 backdrop-blur-sm mb-6" data-testid="hero-rating-badge">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-cyan-400 text-cyan-400" />
                ))}
              </div>
              <span className="text-xs font-semibold text-zinc-300">5.0 · 15 Google Reviews</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.95]" data-testid="hero-headline">
              About <span className="text-cyan-400">B&amp;B Auto Detailing</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed" data-testid="hero-subhead">
              My name is Brian Rosales and I am a college student at SRJC. I've
              been doing car detailing for a little over a year now, and to me
              it's more than just a job — it's something I'm very passionate
              about and genuinely enjoy doing. I treat every car with care and
              precision, and I appreciate everybody that gives me a chance. My
              goal is to offer great results, friendly service, honest pricing,
              and give people that <span className="text-cyan-400 font-semibold">"new-car feeling."</span>
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#booking"
                data-testid="hero-book-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-cyan-400 text-zinc-950 font-bold hover:bg-cyan-300 transition-all shadow-[0_0_30px_rgba(34,211,238,0.4)] hover:shadow-[0_0_40px_rgba(34,211,238,0.7)] hover:-translate-y-0.5"
              >
                <Sparkles className="h-5 w-5" />
                Book Your Detail
              </a>
              <a
                href="tel:+17079090231"
                data-testid="hero-call-btn"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-zinc-700 text-zinc-100 font-semibold hover:border-cyan-400 hover:text-cyan-400 transition-all"
              >
                <Phone className="h-5 w-5" />
                Call / Text (707) 909-0231
              </a>
            </div>

            {/* Trust strip */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-zinc-400">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-cyan-400" />
                Santa Rosa &amp; Sonoma County
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cyan-400" />
                Open until 8 PM
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-cyan-400 fill-cyan-400" />
                Top-rated locally
              </div>
            </div>
          </div>

          {/* Right side stat card (desktop) */}
          <div className="hidden lg:block lg:col-span-4 fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 backdrop-blur-md p-8 shadow-[0_0_60px_rgba(34,211,238,0.08)]">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400 mb-3">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold tracking-tight">Full Detail</h3>
              <p className="text-sm text-zinc-400 mt-1">Inside &amp; out, top to bottom.</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-6xl font-black tracking-tighter text-cyan-400">$110</span>
                <span className="text-sm text-zinc-500">starting</span>
              </div>
              <div className="mt-6 pt-6 border-t border-zinc-800 space-y-2 text-sm text-zinc-300">
                <div className="flex items-center gap-2">
                  <span className="text-cyan-400">✓</span> Deep interior shampoo
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-cyan-400">✓</span> Foam wash &amp; wax
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-cyan-400">✓</span> Tire shine &amp; trim dressing
                </div>
              </div>
              <a
                href="#booking"
                className="mt-6 block text-center bg-cyan-400 text-zinc-950 font-bold py-3 rounded-full hover:bg-cyan-300 transition-all"
                data-testid="hero-card-book-btn"
              >
                Reserve a Slot
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
